import {
  classTimeTypeLabelMap,
  classTimeTypeSchema,
  lessonKindSchema,
  LessonKindToLabel,
  coachSessionRemarkSchema,
  CoachSessionRemarkLabelMap,
  MeetingStatusLabelMap,
  meetingStatusSchema,
} from '@airreading/common/data';
import { DefaultValues, Path, PathValue, UseFormReturn } from 'react-hook-form';
import { z } from 'zod';

export type LabelMap<T extends string> = Record<T, string>;

export interface FormOption<T extends string> {
  value: T;
  label: string;
}

/**
 * Creates an array of options for form components (select, radio, etc.) from an enum-like type
 * and its corresponding label map.
 *
 * @example
 * ```typescript
 * const options = createFormOptions(userRoleSchema.options, UserRoleLabelMap);
 * // Use with radio groups
 * <FormRadioGroup options={options} />
 * // Use with select
 * <FormSelect options={options} />
 * ```
 */
export function createFormOptions<T extends string>(values: T[], labelMap: LabelMap<T>): FormOption<T>[] {
  return values.map((value) => ({
    value,
    label: labelMap[value],
  }));
}

export const classTimeTypesFilterOption = createFormOptions(classTimeTypeSchema.options, classTimeTypeLabelMap);
export const meetingStatusesFilterOption = createFormOptions(meetingStatusSchema.options, MeetingStatusLabelMap);
export const lessonKindsFilterOption = createFormOptions(lessonKindSchema.options, LessonKindToLabel);
export const coachSessionRemarksFilterOption = createFormOptions(
  coachSessionRemarkSchema.options,
  CoachSessionRemarkLabelMap,
);

export type BaseFormSchema = z.ZodObject<z.ZodRawShape>;
export type FormSchema = BaseFormSchema | z.ZodEffects<BaseFormSchema>;

export const useReset = <S extends FormSchema>(
  form: UseFormReturn<z.infer<S>>,
  defaultValues?: DefaultValues<z.infer<S>>,
) => {
  const { getValues, setValue } = form;
  const fields = Object.keys(getValues()) as Path<z.infer<S>>[];

  return (newValues: Partial<z.infer<S>> = {}) => {
    fields.forEach((field) => {
      const value = newValues[field] ?? defaultValues?.[field];
      setValue(field, value as PathValue<z.infer<S>, Path<z.infer<S>>>);
    });
  };
};
